// Язык интерфейса и загрузка каталогов — ДО кода приложения (main.tsx → initI18n → boot.tsx).
// Поэтому t() можно звать где угодно, даже в константах модулей; смена языка = перезагрузка.
//
// Какой язык (решение владельца 03.10):
//   1. ?lang=xx в адресе — на сессию вкладки (проверка beta-языков и псевдолокали xx);
//   2. сохранённый выбор (localStorage + cookie sf_ui_lang) — дальше человек видит тот же язык;
//   3. первый вход — ТОЛЬКО страна по IP (/web/site/mirror из инлайн-скрипта index.html), язык браузера
//      не смотрим; нет языка страны в статусе on или IP не определился → en. Результат сохраняется (auto).
// После входа UI = родной язык (languageStore.initializeLanguage), если он в статусе on.
import i18n from '../locales/i18n'
import {
  FALLBACK_LANG,
  PSEUDO_LANG,
  SOURCE_LANG,
  isListedLang,
  isLoadableLang,
  langInfo,
} from './manifest'
import { pseudo } from './pseudo'

const STORAGE_KEY = 'sf_ui_lang'            // "<код>|auto" | "<код>|explicit" | "<код>|user"
const OVERRIDE_KEY = 'sf_ui_lang_override'  // ?lang= — на сессию вкладки
const LEGACY_KEY = 'language-storage'       // старый zustand-persist (язык из браузера)
const GEO_WAIT_MS = 2500

// auto — по стране IP; explicit — выбор гостя на лендинге (после входа уступает родному языку);
// user — выбор вошедшего человека в меню: важнее родного языка («дальше — его выбор»)
export type How = 'auto' | 'explicit' | 'user'

declare global {
  interface Window {
    __sfGeo?: Promise<{ ui_lang?: string | null; country?: string | null } | null>
  }
}

const catalogs = import.meta.glob('../locales/*/*.json', { import: 'default' }) as Record<
  string,
  () => Promise<Record<string, unknown>>
>

function safe<T>(fn: () => T): T | null {
  try {
    return fn()
  } catch {
    return null
  }
}

export function savedLang(): { code: string; how: How } | null {
  const raw =
    safe(() => localStorage.getItem(STORAGE_KEY)) ??
    safe(() => document.cookie.match(/(?:^|;\s*)sf_ui_lang=([^;]+)/)?.[1] ?? null)
  if (!raw) return null
  const [code, how] = decodeURIComponent(raw).split('|')
  return code ? { code, how: how === 'explicit' || how === 'user' ? how : 'auto' } : null
}

export function saveLang(code: string, how: How): void {
  const v = `${code}|${how}`
  safe(() => localStorage.setItem(STORAGE_KEY, v))
  const secure = location.protocol === 'https:' ? '; Secure' : ''
  safe(() => {
    document.cookie = `sf_ui_lang=${encodeURIComponent(v)}; path=/; max-age=31536000; SameSite=Lax${secure}`
  })
}

/** ?lang=xx → на сессию вкладки (не перекрывает сохранённый выбор). */
export function sessionOverride(): string | null {
  const q = safe(() => new URLSearchParams(location.search).get('lang'))
  if (q) {
    if (q === 'off') {
      safe(() => sessionStorage.removeItem(OVERRIDE_KEY))
      return null
    }
    if (isLoadableLang(q)) {
      safe(() => sessionStorage.setItem(OVERRIDE_KEY, q))
      return q
    }
  }
  const s = safe(() => sessionStorage.getItem(OVERRIDE_KEY))
  return s && isLoadableLang(s) ? s : null
}

function legacyLang(): string | null {
  const raw = safe(() => localStorage.getItem(LEGACY_KEY))
  if (!raw) return null
  const code = safe(() => JSON.parse(raw)?.state?.currentLanguage as string | undefined)
  return code && isListedLang(code) ? code : null
}

/** Язык по стране IP. answered=false — сервер не ответил вовремя: en на этот раз, без запоминания. */
async function geoLang(): Promise<{ code: string | null; answered: boolean }> {
  const p = window.__sfGeo
  if (!p) return { code: null, answered: false }
  // таймер снимается сразу после ответа: висящий таймер — лишняя работа (и headless-скриншоты
  // с --virtual-time-budget проматывают время до него, не дождавшись загрузки модулей)
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<undefined>((r) => {
    timer = setTimeout(() => r(undefined), GEO_WAIT_MS)
  })
  const d = await Promise.race([p.catch(() => null), timeout])
  clearTimeout(timer)
  if (!d) return { code: null, answered: false }
  const code = d.ui_lang
  return { code: code && isListedLang(code) ? code : null, answered: true }
}

export async function decideLang(): Promise<string> {
  const override = sessionOverride()
  if (override) return override
  const saved = savedLang()
  if (saved && (isListedLang(saved.code) || (saved.how !== 'auto' && isLoadableLang(saved.code)))) {
    return saved.code
  }
  const legacy = legacyLang()
  if (legacy) {
    saveLang(legacy, 'auto')
    return legacy
  }
  const geo = await geoLang()
  const lang = geo.code ?? FALLBACK_LANG
  if (geo.answered) saveLang(lang, 'auto')
  return lang
}

/** Цепочка фолбэков: de → en → ru → текст из кода. Для ru — только код и ru-каталог. */
export function fallbackChain(code: string): string[] {
  if (code === SOURCE_LANG) return []
  if (code === PSEUDO_LANG || code === FALLBACK_LANG) return [SOURCE_LANG]
  return [FALLBACK_LANG, SOURCE_LANG]
}

async function loadLanguage(code: string): Promise<Record<string, Record<string, unknown>>> {
  const out: Record<string, Record<string, unknown>> = {}
  const prefix = `../locales/${code}/`
  await Promise.all(
    Object.entries(catalogs)
      .filter(([path]) => path.startsWith(prefix))
      .map(async ([path, load]) => {
        const ns = path.slice(prefix.length, -'.json'.length)
        out[ns] = await load()
      }),
  )
  return out
}

let current = SOURCE_LANG

export async function initI18n(): Promise<string> {
  // Сбой выбора/загрузки не должен оставить i18next неинициализированным (t() вернул бы
  // undefined — пустые кнопки): в худшем случае — тексты из кода (русский).
  const lang = await decideLang().catch(() => FALLBACK_LANG)
  const chain = fallbackChain(lang)
  const codes = [lang, ...chain].filter((c) => c !== PSEUDO_LANG)
  const loaded = await Promise.all(codes.map((c) => loadLanguage(c).catch(() => ({}))))
  const resources: Record<string, Record<string, Record<string, unknown>>> = {}
  const ns = new Set<string>(['common'])
  codes.forEach((c, i) => {
    resources[c] = loaded[i]
    Object.keys(loaded[i]).forEach((n) => ns.add(n))
  })
  if (lang === PSEUDO_LANG) {
    i18n.use({ type: 'postProcessor', name: 'pseudo', process: (value: string) => pseudo(value) })
  }
  await i18n.init({
    resources,
    lng: lang,
    fallbackLng: (code: string) => fallbackChain(code),
    ns: [...ns],
    defaultNS: 'common',
    nsSeparator: ':',
    keySeparator: '.',
    returnEmptyString: false,
    returnNull: false,
    interpolation: { escapeValue: false },
    postProcess: lang === PSEUDO_LANG ? ['pseudo'] : false,
    react: { useSuspense: false },
  })
  current = lang
  const html = document.documentElement
  html.lang = lang === PSEUDO_LANG ? SOURCE_LANG : langInfo(lang)?.locale ?? lang
  html.dir = langInfo(lang)?.dir ?? 'ltr'
  return lang
}

/** Текущий язык интерфейса (код: ru, en, de…; xx — псевдолокаль). */
export const uiLang = () => current

/** Локаль для Intl (даты, числа): de-DE, pt-BR… */
export const uiLocale = () => (current === PSEUDO_LANG ? 'ru-RU' : langInfo(current)?.locale ?? current)

/** Сменить язык: запомнить и перезагрузить оболочку (никаких «застрявших» строк в константах). */
export function switchLang(code: string, how: How = 'explicit'): void {
  if (!isLoadableLang(code)) return
  saveLang(code, how)
  safe(() => sessionStorage.removeItem(OVERRIDE_KEY))
  if (code !== current) location.reload()
}

/** ?lang= активен — родной язык пользователя его не перекрывает. */
export const hasSessionOverride = () => !!safe(() => sessionStorage.getItem(OVERRIDE_KEY))
